import * as React from 'react';
import { useState, useEffect } from 'react';

/** Material UI Packages. */
import { Box, Typography } from '@mui/material';
import { randomId } from '@mui/x-data-grid-generator';
import { useGridApiRef } from '@mui/x-data-grid-premium';

/** Material UI Icons. */
import AddIcon from '@mui/icons-material/Add';

/** Redux Packages. */
import { useSelector, useDispatch } from 'react-redux';

/** Redux Slices. */
import { setSnackbar } from 'shared/store/slices/snackBarSlice';

/** Internal Components. */
import { MainDatagrid, handleTabKeyDown } from 'shared/ui/Datagrid';
import ThemeButton from 'shared/ui/Button';
import {
    readSocketData,
    createSocketData,
    updateSocketData,
    deleteSocketData,
    handleProcessRowUpdate,
} from 'shared/lib/fetchSocket';
import { reducerName, keyName } from 'data/disputeConfig';

import { getColumns } from './DisputeDef';

const Dispute = (props) => {
    /** Get props from ShipmentDetail.js */
    const { row } = props;

    /** Get the dispatch function from Redux to send actions to the store. */
    const dispatch = useDispatch();

    const apiRef = useGridApiRef();

    /** Get the currently selected shipment type value. */
    const tabValue = useSelector((state) => state.tabValueForm);
    /** shipment progress status (inprogress/complete/dispute). */
    const shipmentProgress = useSelector((state) => state.progressValue);

    /** Function to dynamically select a state. */
    const useDynamicSelector = (stateKey) => {
        const dynamicStateKey = `${stateKey}`;
        return useSelector((state) => state[dynamicStateKey]);
    };
    /** Get dispute data, HBL / Container / Alias data. */
    const disputeData = useDynamicSelector(keyName[tabValue][1]);
    const hblList = useDynamicSelector(keyName[tabValue][2]);
    const cntrList = useDynamicSelector(keyName[tabValue][3]);
    const aliasList = useDynamicSelector(keyName[tabValue][4]);

    /** State for storing dispute row data. */
    const [disputeRow, setDisputeRow] = useState([]);

    /** State for storing trade partner data. */
    const [tradePartnerOptions, setTradePartnerOptions] = useState([]);

    /** Hook to filter dispute data for each shipment type and populate the DataGrid rows with the filtered data. */
    useEffect(() => {
        if (!disputeData) return;

        const matchedKeyname = keyName[tabValue][0];
        const disputeDetail = disputeData
            .filter((item) => item[matchedKeyname] === row.id)
            .map((item) => item.il_dispute_detail);

        setDisputeRow(disputeDetail);
    }, [disputeData, row.id]);

    /** Handle to add a row into dispute table. */
    const handleAddDispute = () => {
        const matchedKeyname = keyName[tabValue][0];
        const matchedReducer = reducerName[tabValue][1];
        const newId = randomId();
        const newDispute = {
            id: newId,
            [matchedKeyname]: row.id,
            il_dispute_detail: {
                dispute_date: null,
                dispute_of: '',
                dispute_of_name: '',
                dispute_type: '',
                il_trade_partner_id: '',
                dispute_original_rate: 0,
                dispute_requested_rate: 0,
                dispute_reason: '',
                dispute_action: '',
                il_dispute_detail_status: '',
            },
        };

        createSocketData(dispatch, tabValue, 'row', 'il_dispute', matchedReducer, newDispute);
    };

    /** Handle to delete a row in dispute table. */
    const handleDisputeDelete = (id) => () => {
        if (window.confirm('Are you sure you want to delete the entry?')) {
            const reducerNames = reducerName[tabValue][2];
            deleteSocketData(dispatch, tabValue, 'row', 'il_dispute', reducerNames, id, () => {
                dispatch(setSnackbar({ open: true, message: 'Successfully deleted.', severity: 'success' }));
            });
        }
    };

    /** Handle to change autocomplete trade partner value and call Websocket to receive the searched result. */
    const handleTradePartnerChange = (event, params) => {
        readSocketData(dispatch, tabValue, 'cell', 'il_dispute', 'il_trade_partner_id', params, (receivedData) => {
            setTradePartnerOptions(receivedData);
        });
    };

    return (
        <>
            {shipmentProgress === 'dispute' || (shipmentProgress === 'complete' && disputeRow.length > 0) ? (
                <Box sx={{ width: '100%', mb: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography sx={{ color: '#003a52', fontSize: '13px', fontWeight: 500, textAlign: 'left' }}>
                            Dispute Status
                        </Typography>
                        <ThemeButton text="NEW" icon={<AddIcon />} width={'68px'} onClick={handleAddDispute} />
                    </Box>
                    <MainDatagrid
                        rows={disputeRow ? disputeRow : []}
                        cols={getColumns(
                            tabValue,
                            handleDisputeDelete,
                            row,
                            hblList,
                            cntrList,
                            aliasList,
                            tradePartnerOptions,
                            handleTradePartnerChange
                        )}
                        processRowUpdate={(newRow) =>
                            handleProcessRowUpdate({
                                dispatch,
                                tabValue,
                                methodTableName: 'il_dispute_detail',
                                reducerName: reducerName[tabValue][0],
                                tableId: [],
                                formData: disputeRow,
                                newRow,
                                notAllowNullKey: [],
                                dateField: ['dispute_date'],
                                // prevRow = 이 필드를 적용하기 직전의 행. 여러 셀 붙여넣기에서
                                // 뒤쪽 필드가 실패해도 앞서 저장된 필드는 화면에 남도록,
                                // 실패 시 prevRow를 반환한다(붙여넣기 이전 원본 행이 아님).
                                onReturn: (response, newRow, prevRow, resolve, key) => {
                                    // dispute_of가 변경되면 dispute_of_name도 빈 문자열로 업데이트
                                    if (response.status === 200) {
                                        if (key === 'dispute_of') {
                                            updateSocketData(
                                                dispatch,
                                                tabValue,
                                                'cell',
                                                'il_dispute_detail',
                                                reducerName[tabValue][0],
                                                { id: newRow.id, key: 'dispute_of_name', value: '' },
                                                () => {
                                                    resolve({ ...newRow, dispute_of_name: '' });
                                                },
                                                () => {
                                                    resolve(prevRow);
                                                }
                                            );
                                        } else {
                                            resolve(newRow);
                                        }
                                    } else {
                                        resolve(prevRow);
                                    }
                                },
                            })
                        }
                        hideFooter={true}
                        id_status={false}
                        showToolbar={'none'}
                        sx={{
                            height: '23vh',
                            p: 0,
                            width: '100%',
                            '& .MuiDataGrid-main': {
                                borderRadius: 2,
                                boxShadow: 2,
                            },
                            '& .MuiDataGrid-columnHeaderTitle': {
                                fontSize: 12,
                            },
                        }}
                        apiRef={apiRef}
                        additional={{ onCellKeyDown: handleTabKeyDown(apiRef) }}
                    />
                </Box>
            ) : (
                <></>
            )}
        </>
    );
};
export default React.memo(Dispute);
